import { MATERIAL_THICKNESS,  BLEED } from "./constants.js"
import { toPx, calculateSecondPointYFromAngleAndDistance } from "./utils/common.js"
import { BOX_TYPES } from "./BoxTypes.js"

import * as svgGenerator from "./svgGenerator/mailingBoxTLGenerator.js";

import Base from "./Base";
import Panel from "./Panel";

class MailingBoxTL extends Base {
  maximumFlapWidth = 0;
  maximumFlapWidthInPx = 0;
  flapWidth = 0;
  flapWidthInPx = 0;
  overlapWidth = 0;
  overlapWidthInPx = 0;

  constructor(params) {
    super(params);

    this.type = BOX_TYPES.MAILER;

    this.overlapWidth = MATERIAL_THICKNESS * 5;
    this.overlapWidthInPx = this.overlapWidth * this.pixelsPerInch;

    this.setFlapDimensions();
    this.setCanvasDimensions();
    this.setControlsSize();
    this.panels = this.getPanels();
  }

  getPanels() {
    const {
      x: guideX,
      y: guideY
    } = this.getGuidePoint()

    // Get box W, L, D, for simplicity
    const {
      width: W,
      length: L,
      depth: D,
    } = this;

    const CLOSING_FLAP_WIDTH = 2.5

    // panel config objects
    let panels = [
      {
        name: "outsideFrontDustFlapLeft",
        row: 'first',
        print: false,
        printInside: false,
        width: this.flapWidth,
        height: D,
        top: BLEED,
        left: function () {
          return guideX - ((3*MATERIAL_THICKNESS + L + 3*MATERIAL_THICKNESS) / 2) - this.width
        },
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "none",
        cameraRotationV: "none",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height + MATERIAL_THICKNESS}
            L ${this.left + this.width - 3*MATERIAL_THICKNESS} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "outsideFrontWall",
        print: true,
        printInside: true,
        row: 'first',
        width: 3*MATERIAL_THICKNESS + L + 3*MATERIAL_THICKNESS,
        height: MATERIAL_THICKNESS + D,
        top: BLEED,
        left: function() {
          return guideX - (this.width / 2)
        },
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "front",
        cameraRotationV: "middle",
        cameraRotationHInside: "back",
        cameraRotationVInside: "high",
        lid: "open",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "outsideFrontDustFlapRight",
        print: false,
        printInside: false,
        row: 'first',
        width: this.flapWidth,
        height: D,
        top: BLEED,
        left: function () {
          return guideX + ((3*MATERIAL_THICKNESS + L + 3*MATERIAL_THICKNESS) / 2)
        },
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "none",
        cameraRotationV: "none",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left + 3*MATERIAL_THICKNESS} ${this.top + this.height}
            L ${this.left} ${this.top + this.height + MATERIAL_THICKNESS}
            Z
          `
        }

      },
      {
        name: "insideLeftWall",
        print: true,
        printInside: false,
        row: 'second',
        width: D + MATERIAL_THICKNESS,
        height: W,
        top: function() {
          return guideY - (this.height / 2)
        },
        left: BLEED,
        angle: 90,
        insideAngle: 0,
        cameraRotationH: "middleRight",
        cameraRotationV: "high",
        lid: "open",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "leftOverlap",
        print: true,
        printInside: false,
        row: 'second',
        width: 5*MATERIAL_THICKNESS,
        height: 2*MATERIAL_THICKNESS + W + 2*MATERIAL_THICKNESS,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + D + MATERIAL_THICKNESS,
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "left",
        cameraRotationV: "high",
        lid: "closed",
        path: function () {
          return `
            M ${this.left} ${this.top + MATERIAL_THICKNESS * 2}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${(this.top + this.height) - MATERIAL_THICKNESS * 2}
            Z
          `
        }
      },
      {
        name: "outsideLeftWall",
        print: true,
        printInside: false,
        row: 'second',
        width: MATERIAL_THICKNESS + D + MATERIAL_THICKNESS,
        height: 2*MATERIAL_THICKNESS + W + 2*MATERIAL_THICKNESS,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + D + MATERIAL_THICKNESS + this.overlapWidth,
        angle: -90,
        insideAngle: 0,
        cameraRotationH: "left",
        cameraRotationV: "middle",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      // @TODO: Rename panel
      {
        name: "outsideBottom",
        print: true,
        printInside: true,
        row: 'second',
        width: 7*MATERIAL_THICKNESS + L + 7*MATERIAL_THICKNESS,
        height: 2*MATERIAL_THICKNESS + W + 2*MATERIAL_THICKNESS,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + (D + MATERIAL_THICKNESS) + this.overlapWidth + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS),
        angle: 0,
        insideAngle: 180,
        cameraRotationH: "front",
        cameraRotationV: "low",
        cameraRotationHInside: "front",
        cameraRotationVInside: "top",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + 4*MATERIAL_THICKNESS} ${this.top + MATERIAL_THICKNESS}
            h ${this.width - 8*MATERIAL_THICKNESS}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left + this.width - 2*MATERIAL_THICKNESS} ${this.top + this.height - MATERIAL_THICKNESS}
            h ${-(this.width - 4*MATERIAL_THICKNESS)}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "outsideRightWall",
        print: true,
        printInside: false,
        row: 'second',
        width: MATERIAL_THICKNESS + D + MATERIAL_THICKNESS,
        height: 2*MATERIAL_THICKNESS + W + 2*MATERIAL_THICKNESS,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + (D + MATERIAL_THICKNESS) + this.overlapWidth + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS) + (7*MATERIAL_THICKNESS + L + 7*MATERIAL_THICKNESS),
        angle: 90,
        insideAngle: 0,
        cameraRotationH: "right",
        cameraRotationV: "middle",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "rightOverlap",
        print: true,
        printInside: false,
        width: this.overlapWidth,
        row: 'second',
        height: 2*MATERIAL_THICKNESS + W + 2*MATERIAL_THICKNESS,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + (D + MATERIAL_THICKNESS) + this.overlapWidth + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS) + (7*MATERIAL_THICKNESS + L + 7*MATERIAL_THICKNESS) + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS),
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "right",
        cameraRotationV: "high",
        lid: "closed",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top + 2*MATERIAL_THICKNESS}
            L ${this.left + this.width} ${this.top + this.height - 2*MATERIAL_THICKNESS}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "insideRightWall",
        print: true,
        printInside: false,
        row: 'second',
        width: D + MATERIAL_THICKNESS,
        height: W,
        top: function () {
          return guideY - (this.height / 2)
        },
        left: BLEED + (D + MATERIAL_THICKNESS) + this.overlapWidth + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS) + (7*MATERIAL_THICKNESS + L + 7*MATERIAL_THICKNESS) + (MATERIAL_THICKNESS + D + MATERIAL_THICKNESS) + this.overlapWidth,
        angle: -90,
        insideAngle: 0,
        cameraRotationH: "middleLeft",
        cameraRotationV: "high",
        lid: "open",
        // @NOTE WIP
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "outsideBackWallDustFlapLeft",
        print: false,
        printInside: false,
        row: 'third',
        width: this.flapWidth,
        height: D,
        top: this.bleed + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS,
        left: function() {
          return guideX - ((5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS) / 2) - this.width
        },
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "none",
        cameraRotationV: "none",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width - 2*MATERIAL_THICKNESS} ${this.top}
            L ${this.left + this.width} ${this.top - MATERIAL_THICKNESS}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "outsideBackWall",
        print: true,
        printInside: true,
        row: 'third',
        width: 5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS,
        height: MATERIAL_THICKNESS + D + MATERIAL_THICKNESS,
        top: this.bleed + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS,
        left: function () {
          return guideX - (this.width / 2)
        },
        angle: 180,
        insideAngle: 180,
        cameraRotationH: "back",
        cameraRotationV: "middle",
        cameraRotationHInside: "front",
        cameraRotationVInside: "high",
        lid: "closed",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height - MATERIAL_THICKNESS}
            L ${this.left + this.width - 4*MATERIAL_THICKNESS} ${this.top + this.height}
            h ${-(this.width - 8*MATERIAL_THICKNESS)}
            L ${this.left} ${this.top + this.height - MATERIAL_THICKNESS}
            Z
          `
        }
      },
      {
        name: "outsideBackWallDustFlapRight",
        print: false,
        printInside: false,
        row: 'third',
        width: this.flapWidth,
        height: D,
        top: this.bleed + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS,
        left: function() {
          return guideX + ((5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS) / 2)
        },
        angle: 0,
        insideAngle: 0,
        cameraRotationH: "none",
        cameraRotationV: "none",
        lid: "current",
        path: function () {
          return `
            M ${this.left} ${this.top - MATERIAL_THICKNESS}
            L ${this.left + 2*MATERIAL_THICKNESS} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "leftDustFlapOutside",
        print: true,
        printInside: true,
        row: 'fourth',
        width: D,
        height: W,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + 2*MATERIAL_THICKNESS,
        left: function () {
          // Top side panel width is in perentecies
          return guideX - ((MATERIAL_THICKNESS + L + MATERIAL_THICKNESS) / 2) - this.width
        },
        angle: -90,
        insideAngle: 90,
        cameraRotationH: "left",
        cameraRotationV: "middle",
        cameraRotationHInside: "right",
        cameraRotationVInside: "high",
        lid: "open",
        point1: function () {
          return calculateSecondPointYFromAngleAndDistance(
            (this.left + this.width),
            (this.top),
            this.left + 1.15,
            -5)
        },
        point4: function () {
          return calculateSecondPointYFromAngleAndDistance(
            (this.left + this.width),
            (this.top + this.height),
            this.left + 1.15,
            20)
        },
        path: function () {
          return `
            M ${this.point1.x} ${this.point1.y}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.point4.x} ${this.point4.y}
            Q ${this.point4.x - 1.1} ${this.point4.y - 0.45} ${this.point4.x - 1.15} ${this.point4.y - 1.15}
            L ${this.point1.x - 1.15} ${this.point1.y + 1.15}
            Q ${this.point1.x - 0.9} ${this.point1.y + 0.25} ${this.point1.x} ${this.point1.y}
            Z
          `
        }

        // M ${this.left} ${this.top}
        // L ${this.point2.x} ${this.point2.y}
        // Q ${this.point2.x + 1.15} ${this.point2.y + 0.35} ${this.point2.x + 1.15} ${this.point2.y + 1.15}
        // L ${this.point3.x + 1.15} ${this.point3.y - 1.15}
        // Q ${this.point3.x + 1.15} ${this.point3.y - 0.25} ${this.point3.x} ${this.point3.y}
        // L ${this.left} ${this.top + this.height}
        // Z

      },
      {
        name: "topSide",
        print: true,
        printInside: true,
        row: 'fourth',
        width: MATERIAL_THICKNESS + L + MATERIAL_THICKNESS,
        height: 3*MATERIAL_THICKNESS + W + MATERIAL_THICKNESS,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS,
        left: function () {
          return guideX - this.width / 2
        },
        angle: 0,
        insideAngle: 180,
        cameraRotationH: "front",
        cameraRotationV: "high",
        cameraRotationHInside: "front",
        cameraRotationVInside: "high",
        lid: "closed",
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "rightDustFlapOutside",
        print: true,
        printInside: true,
        row: 'fourth',
        width: D,
        height: W,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + 2*MATERIAL_THICKNESS,
        left: function () {
          // Top side panel width is in perentecies
          return guideX + ((MATERIAL_THICKNESS + L + MATERIAL_THICKNESS) / 2)
        },
        angle: 90,
        insideAngle: -90,
        cameraRotationH: "right",
        cameraRotationV: "middle",
        cameraRotationHInside: "left",
        cameraRotationVInside: "high",
        lid: "open",
        point2: function () {
          return calculateSecondPointYFromAngleAndDistance(
            (this.left),
            (this.top),
            this.left + this.width - 1.15 ,
            5
          )
        },
        point3: function() {
          return calculateSecondPointYFromAngleAndDistance(
            (this.left),
            (this.top + this.height),
            this.left + this.width - 1.15,
            -20
          )
        },
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.point2.x} ${this.point2.y}
            Q ${this.point2.x + 0.95} ${this.point2.y + 0.35} ${this.point2.x + 1.15} ${this.point2.y + 1.15}
            L ${this.point3.x + 1.15} ${this.point3.y - 1.15}
            Q ${this.point3.x + 0.95} ${this.point3.y - 0.45} ${this.point3.x} ${this.point3.y}
            L ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
      {
        name: "leftClosingFlap",
        print: true,
        printInside: true,
        row: 'fifth',
        width: CLOSING_FLAP_WIDTH,
        height: D - 2*MATERIAL_THICKNESS,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + 3*MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + 2*MATERIAL_THICKNESS,
        left: function () {
          // Front flap outside panel width is in perentecies
          return guideX - ((5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS) / 2) - this.width
        },
        angle: -90,
        insideAngle: 90,
        cameraRotationH: "backLeft",
        cameraRotationV: "middle",
        cameraRotationHInside: "right",
        cameraRotationVInside: "high",
        lid: "open",
        // @NOTE WIP
        path: function () {
          return `
            M ${this.left + 0.65} ${this.top}
            L ${this.left + this.width} ${this.top}
            L ${this.left + this.width} ${this.top + this.height}
            Q ${this.left + 0.15} ${this.top + this.height - 0.15} ${this.left} ${this.top + this.height - 1.5}
            L ${this.left} ${this.top + 0.65}
            Q ${this.left + 0.25} ${this.top} ${this.left + 0.65} ${this.top}
            Z
          `
        }
      },
      {
        name: "frontFlapOutside",
        print: true,
        printInside: true,
        row: 'fifth',
        width: 5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS,
        height: D,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + 3*MATERIAL_THICKNESS + W + MATERIAL_THICKNESS,
        left: function () {
          return guideX - this.width / 2
        },
        angle: 0,
        insideAngle: 180,
        cameraRotationH: "front",
        cameraRotationV: "middle",
        cameraRotationHInside: "front",
        cameraRotationVInside: "high",
        lid: "closed",
        // @NOTE WIP
        path: function () {
          return `
            M ${this.left + 4*MATERIAL_THICKNESS} ${this.top}
            L ${this.left + this.width - 4*MATERIAL_THICKNESS} ${this.top}
            L ${this.left + this.width - 4*MATERIAL_THICKNESS} ${this.top + 2*MATERIAL_THICKNESS}
            L ${this.left + this.width} ${this.top + 2*MATERIAL_THICKNESS}
            L ${this.left + this.width} ${this.top + this.height}
            L ${this.left} ${this.top + this.height}
            L ${this.left} ${this.top + 2*MATERIAL_THICKNESS}
            L ${this.left + 4*MATERIAL_THICKNESS} ${this.top + 2*MATERIAL_THICKNESS}
            Z
          `
        }
      },
      {
        name: "rightClosingFlap",
        print: true,
        printInside: true,
        row: 'fifth',
        width: CLOSING_FLAP_WIDTH,
        height: D - 2*MATERIAL_THICKNESS,
        top: BLEED + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + MATERIAL_THICKNESS + D + MATERIAL_THICKNESS + 3*MATERIAL_THICKNESS + W + MATERIAL_THICKNESS + 2*MATERIAL_THICKNESS,
        left: function () {
          // Front flap outside panel width is in perentecies
          return guideX + ((5*MATERIAL_THICKNESS + L + 5*MATERIAL_THICKNESS) / 2)
        },
        angle: 90,
        insideAngle: -90,
        cameraRotationH: "backRight",
        cameraRotationV: "middle",
        cameraRotationHInside: "quarterLeft",
        cameraRotationVInside: "middleHigh",
        lid: "open",
        // @NOTE WIP
        path: function () {
          return `
            M ${this.left} ${this.top}
            L ${this.left + this.width - 0.65} ${this.top}
            Q ${this.left + this.width - 0.15} ${this.top + 0.15} ${this.left + this.width} ${this.top + 0.65}
            L ${this.left + this.width} ${this.top + this.height - 1.5}
            Q ${this.left + this.width} ${this.top + this.height - 0.15} ${this.left} ${this.top + this.height}
            Z
          `
        }
      },
    ]

    // debug
    // let _panels = panels.map((panel) => new Panel(panel));
    // console.log(_panels[1])

    // initialize and return panels
    return panels.map((panel) => new Panel(panel));
  }

  // @TODO improve
  setFlapDimensions() {
    const bottomHeight = MATERIAL_THICKNESS + this.length + MATERIAL_THICKNESS;
    const outsideWallWidth = MATERIAL_THICKNESS + this.depth + MATERIAL_THICKNESS

    this.flapWidth = Math.min((bottomHeight / 2), outsideWallWidth);

    this.flapWidthInPx = this.flapWidth * this.pixelsPerInch;
  }

  /**
   * Calculates the total width of the SVG document for the mailer box.
   * This width includes all panels and optionally the bleed areas.
   *
   * Calculation Formula:
   * ```
   * B + (3MT + D + MT) + (5MT) + (MT + D + MT) + (7MT + W + 7MT) + (MT + D + MT) + (5MT) + (MT + D + 3MT) + B
   * => 2B + 36MT + 4D + W
   * ```
   * Where:
   * - B (Bleed): Additional space around the document for printing purposes, in inches.
   * - MT (Material Thickness): Thickness of the material used, in inches.
   * - D (Depth): Depth of the box, in inches.
   * - W (Width): Width of the box, in inches.
   *
   * @param {boolean} withBleed - Optional. Whether to include bleed in the width calculation. Defaults to true.
   * @return {number} The total width of the document, in inches.
   */
  getDocumentWidth(withBleed = true) {
    let _bleed = withBleed ? 2 * this.bleed : 0
    return _bleed + 30 * MATERIAL_THICKNESS + 4 * this.depth + this.length;
  }

  /**
   * Calculates the total height of the SVG document for the mailer box.
   * This hight includes all panels and optionally the bleed.
   *
   * Calculation Formula:
   * ```
   * B + (D) + (3MT + L + MT) + (MT + D + MT) + (MT + L + MT) + (MT + D) + B
   * => 2B + 9MT + 3D + 2L
   * ```
   * Where:
   * - B (Bleed): Additional space around the document for printing purposes, in inches.
   * - MT (Material Thickness): Thickness of the material used, in inches.
   * - D (Depth): Depth of the box, in inches.
   * - L (Lenght): Lenght of the box, in inches.
   *
   * @param {boolean} withBleed - Optional. Whether to include bleed in the height calculation. Defaults to true.
   * @return {number} The total height of the document, in inches.
   */
  getDocumentHeight(withBleed = true) {
    let _bleed = withBleed ? 2 * this.bleed : 0
    return _bleed + 9 * MATERIAL_THICKNESS + 3 * this.depth + 2 * this.width;
  }

  /**
   * Calculates the X coordinate of the vertical guide line.
   * The guide line is positioned at the horizontal midpoint of the document.
   *
   * @TODO rename to something like 'guideX'
   *
   * @return {number} The X coordinate for the vertical guide line.
   */
  getGuideX() {
    return this.getDocumentWidth() / 2;
  }

  /**
   * Calculates the Y coordinate of the horizontal guide line.
   * The guide line is positioned at the vertical midpoint of the bottom panel (bottom side of the box).
   *
   * @TODO rename to something like 'guideY'
   *
   * @return {number} The Y coordinate for the horizontal guide line.
   */
  getGuideY() {
    return this.bleed + (MATERIAL_THICKNESS + this.depth) + (MATERIAL_THICKNESS + this.width + MATERIAL_THICKNESS) / 2;
  }

  /**
   * Retrieves the X and Y coordinates for the guide point, which determines where guide lines intersect.
   *
   * @TODO rename to something like 'guidePoint'
   *
   * @return {{x: number, y: number}} An object containing both X and Y coordinates for the guide point.
   */
  getGuidePoint() {
    return {
      x: this.getGuideX(),
      y: this.getGuideY(),
    }
  }

  setCanvasDimensions() {
    // canvas width
    this.canvasWidth = toPx(this.getDocumentWidth());
    // console.log('document width (in)', this.getDocumentWidth());
    // console.log('canvas width (px)', this.canvasWidth);

    // canvas height
    this.canvasHeight = toPx(this.getDocumentHeight());
    // console.log('document height (in)', this.getDocumentHeight());
    // console.log('canvas height (px)', this.canvasHeight);

    // normalized canvas ratio
    // value between 0 and 1
    this.ratio = this.canvasWidth > this.canvasHeight ?
      this.canvasHeight / this.canvasWidth : this.canvasWidth / this.canvasHeight;
  }

  getGeometrySvg() {
    return svgGenerator.getSvgForGeom(this)
  }

  get2DEditorSvg(printSide = 'outside') {
    return svgGenerator.get2DEditorSvg(this, printSide)
  }

  getOutlinePathWithBleed() {
    return svgGenerator.getOutlineWithBleed(this, false)
  }

  getNormalizedOutlinePathWithBleed(printSide = 'outside') {
    return svgGenerator.getOutlineWithBleed(this, true, printSide)
  }

  getOutlinePathWithoutNonPrintPanels(printSide = 'outside') {
    return svgGenerator.getOutlineWithoutNonPrintPanels(this, false, printSide)
  }

  getOutlinePath() {
    return svgGenerator.getOutline(this)
  }

  getCutlinesPath() {
    return svgGenerator.getCutlines(this)
  }

}

export default MailingBoxTL;
